/**
 * Custom Animation Services — Client-side JS (Phase 1, 2026-05-05)
 *
 * Phase 1 changes vs. v1:
 * - Validation fix: server returns field-specific errors; client maps to inline error UI
 * - Success state: replaces form on successful submit instead of silent reset to step 1
 * - No new form fields yet (those land in Phase 2 along with Vercel API rebuild)
 *
 * API_BASE: Vercel deployment for custom-shoots
 */
(function() {
  'use strict';

  const API_BASE = 'https://mco-custom-shoots.vercel.app';

  // ==================== INIT ====================
  document.addEventListener('DOMContentLoaded', init);

  function init() {
    setupMultiStepForm();
    setupTierSelection();
    setupSmoothScroll();
    setupFAQTracking();
    trackPageView();
  }

  // ==================== MULTI-STEP FORM ====================
  function setupMultiStepForm() {
    const form = document.getElementById('cs-submit-form');
    if (!form) return;

    // Next buttons
    form.querySelectorAll('.cs-form__next').forEach(btn => {
      btn.addEventListener('click', () => {
        const currentStep = parseInt(btn.closest('.cs-form__step').dataset.step);
        if (validateStep(currentStep)) {
          clearAllFieldErrors();
          goToStep(currentStep + 1);
          trackEvent('form_step_started', { step: currentStep + 1 });
        }
      });
    });

    // Prev buttons
    form.querySelectorAll('.cs-form__prev').forEach(btn => {
      btn.addEventListener('click', () => {
        const prevStep = parseInt(btn.dataset.prev);
        clearAllFieldErrors();
        goToStep(prevStep);
      });
    });

    // Form submit
    form.addEventListener('submit', handleSubmit);
  }

  function goToStep(stepNum) {
    const form = document.getElementById('cs-submit-form');

    // Update steps
    form.querySelectorAll('.cs-form__step').forEach(step => {
      step.classList.toggle('cs-form__step--active', parseInt(step.dataset.step) === stepNum);
    });

    // Update progress bar
    document.querySelectorAll('.cs-form__progress-step').forEach(el => {
      const s = parseInt(el.dataset.step);
      el.classList.remove('cs-form__progress-step--active', 'cs-form__progress-step--completed');
      if (s === stepNum) el.classList.add('cs-form__progress-step--active');
      else if (s < stepNum) el.classList.add('cs-form__progress-step--completed');
    });

    // Scroll form into view
    const formSection = document.getElementById('cs-form');
    if (formSection) formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function validateStep(stepNum) {
    const step = document.querySelector(`.cs-form__step[data-step="${stepNum}"]`);
    if (!step) return true;

    const requiredFields = step.querySelectorAll('[required]');
    let valid = true;
    const errors = [];

    requiredFields.forEach(field => {
      if (!field.value.trim()) {
        const fieldName = field.name || 'field';
        const labelText = field.previousElementSibling?.textContent?.replace('*', '').trim() || fieldName;
        setFieldError(fieldName, `${labelText} is required.`);
        field.style.borderColor = '#ef5350';
        errors.push(labelText);
        valid = false;
      } else {
        clearFieldError(field.name);
        field.style.borderColor = '';
      }
    });

    // Email validation on step 1
    if (stepNum === 1) {
      const emailField = step.querySelector('#cs-email');
      if (emailField && emailField.value.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailField.value.trim())) {
          setFieldError('email', 'Please enter a valid email address.');
          emailField.style.borderColor = '#ef5350';
          errors.push('Email format');
          valid = false;
        } else {
          clearFieldError('email');
        }
      }
    }

    if (!valid) {
      const msg = errors.length === 1
        ? `Please fix: ${errors[0]}`
        : `Please fix the highlighted fields above.`;
      showMessage(msg, 'error');
    } else {
      hideMessage();
    }

    return valid;
  }

  // Field-specific error helpers
  function setFieldError(fieldName, message) {
    const errEl = document.querySelector(`[data-error-for="${fieldName}"]`);
    if (errEl) {
      errEl.textContent = message;
      errEl.style.display = '';
    }
  }

  function clearFieldError(fieldName) {
    const errEl = document.querySelector(`[data-error-for="${fieldName}"]`);
    if (errEl) {
      errEl.textContent = '';
      errEl.style.display = 'none';
    }
  }

  function clearAllFieldErrors() {
    document.querySelectorAll('[data-error-for]').forEach(el => {
      el.textContent = '';
      el.style.display = 'none';
    });
    document.querySelectorAll('.cs-form__input, .cs-form__textarea, .cs-form__select').forEach(el => {
      el.style.borderColor = '';
    });
  }

  // ==================== FORM SUBMISSION ====================
  async function handleSubmit(e) {
    e.preventDefault();

    if (!validateStep(3)) return;
    // Also re-validate prior steps in case a user reached step 3 then changed an earlier required field
    if (!validateStep(1) || !validateStep(2)) return;

    const form = e.target;
    const submitBtn = document.getElementById('cs-submit-btn');
    submitBtn.disabled = true;
    submitBtn.textContent = 'Submitting...';

    // Collect all form data
    const formData = {
      contact_name: form.querySelector('#cs-name').value.trim(),
      email: form.querySelector('#cs-email').value.trim(),
      company: form.querySelector('#cs-company')?.value.trim() || '',
      service_tier: form.querySelector('#cs-tier')?.value || '',
      animation_count: form.querySelector('#cs-count')?.value || '',
      description: form.querySelector('#cs-description').value.trim(),
      timeline: form.querySelector('#cs-timeline')?.value || '',
      budget_range: form.querySelector('#cs-budget')?.value || '',
      character_type: form.querySelector('#cs-character')?.value || '',
      referral_source: form.querySelector('#cs-source')?.value || '',
      reference_links: form.querySelector('#cs-references')?.value.trim() || '',
      additional_notes: form.querySelector('#cs-notes')?.value.trim() || '',
    };

    // Collect checked formats
    const formatCheckboxes = form.querySelectorAll('input[name="formats"]:checked');
    formData.formats = Array.from(formatCheckboxes).map(cb => cb.value).join(',');

    // Get A/B variant if running
    const abVariant = getABVariant();
    if (abVariant) formData.ab_variant = abVariant;

    try {
      const resp = await fetch(`${API_BASE}/api/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await resp.json();

      if (data.success) {
        showSuccessState(data.reference_id || data.message || '');
        trackEvent('form_submitted', {
          tier: formData.service_tier,
          budget: formData.budget_range,
        });
      } else {
        // Server-side validation: map field-specific errors
        if (data.errors && typeof data.errors === 'object') {
          Object.entries(data.errors).forEach(([fieldName, errMsg]) => {
            setFieldError(fieldName, errMsg);
            const fieldEl = form.querySelector(`[name="${fieldName}"]`);
            if (fieldEl) fieldEl.style.borderColor = '#ef5350';
          });
          // Jump back to the step containing the first errored field
          const firstErrorField = Object.keys(data.errors)[0];
          const fieldEl = form.querySelector(`[name="${firstErrorField}"]`);
          if (fieldEl) {
            const containingStep = fieldEl.closest('.cs-form__step');
            if (containingStep) {
              const stepNum = parseInt(containingStep.dataset.step);
              goToStep(stepNum);
            }
          }
          showMessage('Please fix the highlighted fields and try again.', 'error');
        } else {
          showMessage(data.error || 'Something went wrong. Please try again or email us at MoCap@MotusDigital.com.', 'error');
        }
        trackEvent('form_error', { error: data.error || 'validation' });
      }
    } catch {
      showMessage('Network error. Please check your connection and try again, or email us at MoCap@MotusDigital.com.', 'error');
      trackEvent('form_error', { error: 'network' });
    }

    submitBtn.disabled = false;
    submitBtn.textContent = 'Submit Request';
  }

  function showMessage(text, type) {
    const el = document.getElementById('cs-form-message');
    if (!el) return;
    el.textContent = text;
    el.className = `cs-form__message cs-form__message--${type}`;
    el.style.display = '';
    // Errors stay visible until the user fixes them or submits again. No auto-hide.
  }

  function hideMessage() {
    const el = document.getElementById('cs-form-message');
    if (el) el.style.display = 'none';
  }

  function showSuccessState(referenceId) {
    // Hide the form, the progress bar, and the inline message
    const form = document.getElementById('cs-submit-form');
    const progress = document.querySelector('.cs-form__progress');
    const successEl = document.getElementById('cs-form-success');

    if (form) {
      form.querySelectorAll('.cs-form__step').forEach(s => s.style.display = 'none');
      form.querySelector('.cs-form__message').style.display = 'none';
    }
    if (progress) progress.style.display = 'none';

    // Populate the reference ID if provided
    if (referenceId) {
      const refEl = document.getElementById('cs-success-ref');
      if (refEl) refEl.textContent = `Reference: ${referenceId}.`;
    }

    if (successEl) {
      successEl.style.display = '';
      // Scroll the success card into view
      successEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  // ==================== TIER SELECTION ====================
  function setupTierSelection() {
    document.querySelectorAll('[data-select-tier]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const tier = e.currentTarget.dataset.selectTier;
        const tierSelect = document.getElementById('cs-tier');
        if (tierSelect) tierSelect.value = tier;
        trackEvent('tier_card_click', { tier });
      });
    });
  }

  // ==================== SMOOTH SCROLL ====================
  function setupSmoothScroll() {
    document.querySelectorAll('a[href^="#cs-"]').forEach(link => {
      link.addEventListener('click', (e) => {
        const target = document.querySelector(link.getAttribute('href'));
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          trackEvent('cta_click', { target: link.getAttribute('href') });
        }
      });
    });
  }

  // ==================== FAQ TRACKING ====================
  function setupFAQTracking() {
    document.querySelectorAll('.cs-faq__item').forEach(item => {
      item.addEventListener('toggle', () => {
        if (item.open) {
          const question = item.querySelector('.cs-faq__question')?.textContent?.trim() || '';
          trackEvent('faq_expanded', { question: question.slice(0, 60) });
        }
      });
    });
  }

  // ==================== A/B TESTING ====================
  function getABVariant() {
    // Cookie-based variant assignment (50/50 split)
    const cookieName = 'cs_ab_variant';
    const existing = getCookie(cookieName);
    if (existing) return existing;

    const variant = Math.random() < 0.5 ? 'A' : 'B';
    setCookie(cookieName, variant, 30); // 30-day cookie
    return variant;
  }

  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : null;
  }

  function setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${value};expires=${expires};path=/;SameSite=Lax`;
  }

  // ==================== GA4 ANALYTICS ====================
  function trackPageView() {
    trackEvent('page_view', { page: 'custom_animation_services' });
  }

  function trackEvent(eventName, params) {
    if (typeof gtag === 'function') {
      gtag('event', eventName, {
        event_category: 'custom_services',
        ...params,
      });
    }
  }

})();
